import {
  decodeSnapshot,
  peekSnapshotTicks,
  ProtocolError,
  type DecodedSnapshot,
} from '@fafi/shared';

const FULL = 0xffffffff;

// Client snapshot store (§10.3). Decodes each incoming snapshot against the baseline it names (a prior
// snapshot the client acked), keeps the last 32 decoded (quantized) snapshots for interpolation brackets
// + future baselines, and tracks the newest tick to ack. TCP ⇒ in-order; a missing baseline is a
// defensive error (never expected on a reliable transport).

export class SnapshotBuffer {
  private readonly byTick = new Map<number, DecodedSnapshot>();
  private readonly order: number[] = [];
  newestTick = -1;
  private readonly cap = 32;

  /** D46 Item 6: drop all buffered snapshots for a NEW match epoch (a rematch restarts at tick 0, so an
   *  old-epoch baseline would mis-delta the fresh stream). */
  reset(): void {
    this.byTick.clear();
    this.order.length = 0;
    this.newestTick = -1;
  }

  /** Decode + store an incoming snapshot. Returns the decoded snapshot (quantized view). */
  ingest(bytes: Uint8Array): DecodedSnapshot {
    const { baselineTick } = peekSnapshotTicks(bytes);
    let baseline: DecodedSnapshot | null = null;
    if (baselineTick !== FULL) {
      baseline = this.byTick.get(baselineTick) ?? null;
      if (!baseline) throw new ProtocolError(`SnapshotBuffer: missing baseline ${baselineTick}`);
    }
    const snap = decodeSnapshot(bytes, baseline);
    if (!this.byTick.has(snap.tick)) this.order.push(snap.tick);
    this.byTick.set(snap.tick, snap);
    if (snap.tick > this.newestTick) this.newestTick = snap.tick;
    while (this.order.length > this.cap) {
      const evicted = this.order.shift()!;
      if (evicted !== this.newestTick) this.byTick.delete(evicted);
    }
    return snap;
  }

  get(tick: number): DecodedSnapshot | undefined {
    return this.byTick.get(tick);
  }
  newest(): DecodedSnapshot | undefined {
    return this.newestTick >= 0 ? this.byTick.get(this.newestTick) : undefined;
  }
  /** Ticks currently held, ascending. */
  ticks(): number[] {
    return [...this.byTick.keys()].sort((a, b) => a - b);
  }
  get size(): number {
    return this.byTick.size;
  }
}
